<template>
  <HKLModal
    ref="modal"
    size="large"
    class="terms-conditions"
    :okBtnDisabled="!accepted && !acceptedByTxt"
    :hasCancelBtn="!acceptedByTxt"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div class="terms-conditions__title">
      <p>{{ $t(`${i18nPrefix}.title`) }}</p>
    </div>

    <div class="terms-conditions__box">
      <div
        v-for="(section, index) in $t(`${i18nPrefix}.general`)"
        :key="index"
        class="terms-conditions__content"
      >
        <div class="terms-conditions__general">
          {{ section.title }}
        </div>
        <div class="terms-conditions__list">
          <div
            v-for="(item, j) in section.content"
            :key="`${index}_${j}`"
            class="terms-conditions__item"
          >
            <span class="terms-conditions__number">{{ j + 1 }}</span>
            <span v-html="item" />
          </div>
        </div>
        <div class="terms-conditions__remarks">
          <div
            v-for="(remark, k) in section.additionalRemarks"
            :key="`${index}__${k}`"
            v-html="remark"
          />
        </div>
      </div>
    </div>

    <div
      class="terms-conditions__acceptance"
    >
      <div
        v-if="acceptedByTxt"
        class="terms-conditions__accepted"
      >
        <div v-html="acceptContent" />
        <div class="agreed-info">
          {{ acceptedByTxt }}
        </div>
      </div>
      <div
        v-else
        class="terms-conditions__checkbox"
      >
        <input
          id="tnc-checkbox"
          v-model="accepted"
          type="checkbox"
        >
        <label
          class="terms-conditions__label"
          for="tnc-checkbox"
          v-html="onBehalfAcceptContent"
        />
      </div>
    </div>
  </HKLModal>
</template>

<script>
import { computed, unref, toRefs, ref, onMounted } from 'vue-demi'
import lodash from 'lodash'
import HKLModal from '@/controls/Modal/HKLModal.vue'
import { useStoreGetters } from '@/store/index'
import { UserDomain } from '@/utils/consts'
import i18n from '@/utils/i18n'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { getBySourceId } from '@/api/histories/request'
import dateUtil from '@/utils/dateUtil'
import { useRouteParamId } from '@/router/useRouter'

export default {
  components: {
    HKLModal,
  },
  props: {
    companyName: {
      type: String,
      default: i18n.t('na'),
    },
  },
  setup (props) {
    const {
      companyName: companyNameRef,
    } = toRefs(props)
    const { regionName } = useStoreGetters('regions', ['regionName'])
    const { isTenant, currentUser } = useStoreGetters('user', ['isTenant', 'currentUser'])
    const { currentWorkPermit } = useStoreGetters('workPermit', ['currentWorkPermit'])
    const i18nDomain = computed(() => {
      const currentWpVal = unref(currentWorkPermit)
      if (currentWpVal.id) {
        return currentWpVal.isTenantProject ? UserDomain.TMS : UserDomain.CMS
      }
      return unref(isTenant) ? UserDomain.TMS : UserDomain.CMS
    })
    const computedCompanyName = computed(() => {
      if (!lodash.isEmpty(unref(companyNameRef))) {
        return unref(companyNameRef)
      } else if (!unref(isTenant)) {
        return lodash.get(unref(currentUser), 'company.name', '')
      } else {
        console.warn('Error: unknown company')
        return i18n.t('na')
      }
    })
    const i18nPrefix = computed(() => {
      return `termsAndConditions.SaWorkPermitDecl.${unref(i18nDomain)}.${unref(regionName)}`
    })
    const title = computed(() => {
      return i18n.t(`${unref(i18nPrefix)}.title`)
    })
    const onBehalfAcceptContent = computed(() => {
      return i18n.t(`${unref(i18nPrefix)}.onBehalfAcceptContent`, [computedCompanyName.value])
    })
    const acceptContent = computed(() => {
      return i18n.t(`${unref(i18nPrefix)}.acceptContent`, [computedCompanyName.value])
    })
    const { modal, open } = useRefModal()
    const accepted = ref(false)
    const acceptedHistory = ref([])
    const acceptedByTxt = computed(() => {
      if (lodash.isEmpty(unref(acceptedHistory))) {
        return ''
      }
      const displayName = lodash.get(unref(acceptedHistory), '0.who.displayName')
      const datetime = lodash.get(unref(acceptedHistory), '0.createdAt')
      return i18n.t('acceptedByOn', [displayName, dateUtil.formatByLongDate(datetime, 'L LT')])
    })
    const wpId = useRouteParamId()
    onMounted(async () => {
      const wpIdVal = unref(wpId)
      if (wpIdVal) {
        acceptedHistory.value = await getBySourceId(wpIdVal, {
          type: 'SA_TNC',
        })
      }
    })
    return {
      currentWorkPermit,
      wpId,
      accepted,
      acceptedByTxt,
      open,
      modal,
      i18nPrefix,
      title,
      onBehalfAcceptContent,
      acceptContent,
    }
  },
}
</script>

<style lang="scss" scoped>
.terms-conditions {
  .error-highlight {
    background-color: #f9ecea;
  }
  &__title {
    p {
      font-size: 16px;
      font-weight: bold;
      line-height: 1.38;
      color: #10416c;
    }
  }
  &__box {
    padding: 10px 20px;
    border: solid 1px #b5b5b5;
    max-height: calc(85vh - 200px);
    overflow-y: scroll;
  }
  &__general {
    font-weight: bold;
  }
  &__item {
    display: flex;
  }
  &__number {
    min-width: 24px;
  }
  &__acceptance {
    margin-top: 20px;
    .company-names {
      margin-bottom: 10px;
    }
    .company-name {
      font-weight: bold;
      margin-right: 5px;
    }
    .agreed-info {
      font-size: 12px;
      color: #6c6767;
      font-style: italic;
    }
    .main-contractor {
      margin-top: 20px;
    }
  }
  &__checkbox {
    display: flex;
    align-items: flex-start;
    margin-top: 20px;
    input {
      min-width: 14px;
    }
    label {
      font-weight: normal;
      margin-right: 0;
      margin-top: -5px;
      .company-name {
        font-weight: bold;
        margin-left: 5px;
        margin-right: 5px;
      }
    }
    margin-bottom: 46px;
  }
}
</style>
